<template>
  <div class="flex gap-1 px-2 text-sm">
    <div
      ref="hourScroller"
      class="-mx-2 flex w-20 shrink-0 flex-col overflow-y-auto overscroll-contain p-2"
      style="max-height: 256px; scrollbar-width: none"
      @scroll="handleHourScroll"
    >
      <button
        v-for="(h, index) in wrappedHourValues"
        :key="index"
        type="button"
        :data-copy="Math.floor(index / hourValues.length)"
        :data-value="h"
        :tabindex="
          Math.floor(index / hourValues.length) === CENTER_COPY &&
          h === focusedHour
            ? 0
            : -1
        "
        :class="[
          'shrink-0 rounded-md py-1.5 text-center tabular-nums outline-2 outline-foreground transition focus-visible:outline',
          h === selectedHour
            ? 'bg-blue-500 font-medium text-white'
            : 'hover:bg-muted',
        ]"
        @click="selectHour(h)"
        @keydown="handleHourKeydown"
      >
        {{ String(h).padStart(2, "0") }}
      </button>
    </div>

    <div
      ref="minuteScroller"
      class="-mx-2 flex w-20 shrink-0 flex-col overflow-y-auto overscroll-contain p-2"
      style="max-height: 256px; scrollbar-width: none"
      @scroll="handleMinuteScroll"
    >
      <button
        v-for="(m, index) in wrappedMinuteValues"
        :key="index"
        type="button"
        :data-copy="Math.floor(index / minuteValues.length)"
        :data-value="m"
        :tabindex="
          Math.floor(index / minuteValues.length) === CENTER_COPY &&
          m === focusedMinute
            ? 0
            : -1
        "
        :class="[
          'shrink-0 rounded-md py-1.5 text-center tabular-nums outline-2 outline-foreground transition focus-visible:outline',
          m === selectedMinute
            ? 'bg-blue-500 font-medium text-white'
            : 'hover:bg-muted',
        ]"
        @click="selectMinute(m)"
        @keydown="handleMinuteKeydown"
      >
        {{ String(m).padStart(2, "0") }}
      </button>
    </div>

    <div
      v-if="is12h"
      ref="periodColumn"
      class="-mx-2 flex w-20 shrink-0 flex-col p-2"
    >
      <button
        type="button"
        :tabindex="selectedPeriod === 0 ? 0 : -1"
        :class="[
          'shrink-0 rounded-md py-1.5 text-center outline-2 outline-foreground transition focus-visible:outline',
          selectedPeriod === 0
            ? 'bg-blue-500 font-medium text-white'
            : 'hover:bg-muted',
        ]"
        @click="selectPeriod(0)"
        @keydown="handlePeriodKeydown"
      >
        {{ amLabel }}
      </button>
      <button
        type="button"
        :tabindex="selectedPeriod === 1 ? 0 : -1"
        :class="[
          'shrink-0 rounded-md py-1.5 text-center outline-2 outline-foreground transition focus-visible:outline',
          selectedPeriod === 1
            ? 'bg-blue-500 font-medium text-white'
            : 'hover:bg-muted',
        ]"
        @click="selectPeriod(1)"
        @keydown="handlePeriodKeydown"
      >
        {{ pmLabel }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, useTemplateRef } from "vue";

import { getDayPeriodLabels, getHourCycle, isValidISOTime } from "@/lib/time";

const modelValue = defineModel<string>({ default: "" });

const props = defineProps<{
  autofocus?: boolean;
}>();

const hourCycle = getHourCycle();
const is12h = hourCycle === "h12" || hourCycle === "h11";
const { am: amLabel, pm: pmLabel } = getDayPeriodLabels();

const hourValues = Array.from({ length: is12h ? 12 : 24 }, (_, i) => {
  return is12h ? i % 12 || 12 : i;
});

const minuteValues = Array.from({ length: 60 }, (_, i) => i);

// Render 5 copies so momentum scrolling on touch devices has room to
// decelerate before hitting the wrap boundary.
const COPY_COUNT = 5;
const CENTER_COPY = Math.floor(COPY_COUNT / 2);

const wrappedHourValues = Array.from(
  { length: COPY_COUNT },
  () => hourValues
).flat();

const wrappedMinuteValues = Array.from(
  { length: COPY_COUNT },
  () => minuteValues
).flat();

const parsed = computed(() => {
  if (!modelValue.value || !isValidISOTime(modelValue.value)) {
    return {
      hour: null as number | null,
      minute: null as number | null,
      period: 0,
    };
  }
  const [h, m] = modelValue.value.split(":").map(Number);
  if (is12h) {
    const period = h >= 12 ? 1 : 0;
    const displayHour = h % 12 || 12;
    return { hour: displayHour, minute: m, period };
  }
  return { hour: h, minute: m, period: 0 };
});

const selectedHour = computed(() => parsed.value.hour);
const selectedMinute = computed(() => parsed.value.minute);
const selectedPeriod = computed(() => parsed.value.period);

const focusedHour = computed(() => selectedHour.value ?? hourValues[0]);
const focusedMinute = computed(() => selectedMinute.value ?? 0);

const emitTime = (hour: number, minute: number, period: number): void => {
  let hour24: number;
  if (is12h) {
    const base = hour === 12 ? 0 : hour;
    hour24 = period === 1 ? base + 12 : base;
  } else {
    hour24 = hour;
  }
  modelValue.value = `${String(hour24).padStart(2, "0")}:${String(
    minute
  ).padStart(2, "0")}`;
};

const selectHour = (h: number): void => {
  emitTime(h, selectedMinute.value ?? 0, selectedPeriod.value);
};

const selectMinute = (m: number): void => {
  emitTime(selectedHour.value ?? (is12h ? 12 : 0), m, selectedPeriod.value);
};

const selectPeriod = (p: number): void => {
  emitTime(
    selectedHour.value ?? (is12h ? 12 : 0),
    selectedMinute.value ?? 0,
    p
  );
};

const hourScroller = useTemplateRef("hourScroller");
const minuteScroller = useTemplateRef("minuteScroller");
const periodColumn = useTemplateRef("periodColumn");

// Matches the py-2 on the scroll containers
const SCROLL_PADDING = 8;

// When the user scrolls into the first or last copy, jump by one copy
// to keep them in the middle 3 copies (safe zone = copies 1–3).
const wrapScroll = (container: HTMLElement): void => {
  const oneSetHeight =
    (container.scrollHeight - SCROLL_PADDING * 2) / COPY_COUNT;
  const scrollPos = container.scrollTop - SCROLL_PADDING;
  if (scrollPos < oneSetHeight) {
    container.scrollTop += oneSetHeight;
  } else if (scrollPos >= oneSetHeight * (COPY_COUNT - 1)) {
    container.scrollTop -= oneSetHeight;
  }
};

const handleHourScroll = (): void => {
  wrapScroll(hourScroller.value!);
};

const handleMinuteScroll = (): void => {
  wrapScroll(minuteScroller.value!);
};

// Scroll so the selected item appears SCROLL_PADDING below the viewport top,
// positioned in the center copy (copy 2 of 0–4).
const scrollToMiddleCopy = (
  container: HTMLElement,
  itemCount: number,
  selectedIndex: number
): void => {
  const itemHeight =
    (container.scrollHeight - SCROLL_PADDING * 2) / (itemCount * COPY_COUNT);
  container.scrollTop =
    CENTER_COPY * itemCount * itemHeight + selectedIndex * itemHeight;
};

// Focus the center copy's button for a given value in a scrollable column.
// If scroll is true, scroll the minimum amount to keep the button visible.
const focusCenterButton = (
  container: HTMLElement,
  value: number,
  scroll = false
): void => {
  const btn = container.querySelector(
    `[data-copy="${CENTER_COPY}"][data-value="${value}"]`
  ) as HTMLElement | null;
  if (!btn) return;

  if (scroll) {
    const containerRect = container.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    const btnTop = btnRect.top - containerRect.top;
    const btnBottom = btnRect.bottom - containerRect.top;
    if (btnTop < SCROLL_PADDING) {
      container.scrollTop += btnTop - SCROLL_PADDING;
    } else if (btnBottom > container.clientHeight - SCROLL_PADDING) {
      container.scrollTop +=
        btnBottom - container.clientHeight + SCROLL_PADDING;
    }
  }

  btn.focus({ preventScroll: true });
};

// Keyboard navigation
const handleHourKeydown = (event: KeyboardEvent): void => {
  const currentIndex = hourValues.indexOf(focusedHour.value);

  switch (event.key) {
    case "ArrowUp": {
      event.preventDefault();
      const prev = (currentIndex - 1 + hourValues.length) % hourValues.length;
      selectHour(hourValues[prev]);
      nextTick(() => {
        focusCenterButton(hourScroller.value!, hourValues[prev], true);
      });
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      const next = (currentIndex + 1) % hourValues.length;
      selectHour(hourValues[next]);
      nextTick(() => {
        focusCenterButton(hourScroller.value!, hourValues[next], true);
      });
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      nextTick(() => {
        focusCenterButton(minuteScroller.value!, focusedMinute.value);
      });
      break;
    }
  }
};

const handleMinuteKeydown = (event: KeyboardEvent): void => {
  const currentIndex = minuteValues.indexOf(focusedMinute.value);

  switch (event.key) {
    case "ArrowUp": {
      event.preventDefault();
      const prev =
        (currentIndex - 1 + minuteValues.length) % minuteValues.length;
      selectMinute(minuteValues[prev]);
      nextTick(() => {
        focusCenterButton(minuteScroller.value!, minuteValues[prev], true);
      });
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      const next = (currentIndex + 1) % minuteValues.length;
      selectMinute(minuteValues[next]);
      nextTick(() => {
        focusCenterButton(minuteScroller.value!, minuteValues[next], true);
      });
      break;
    }
    case "ArrowLeft": {
      event.preventDefault();
      nextTick(() => focusCenterButton(hourScroller.value!, focusedHour.value));
      break;
    }
    case "ArrowRight": {
      if (is12h) {
        event.preventDefault();
        const btn = periodColumn.value?.querySelector(
          "[tabindex='0']"
        ) as HTMLElement | null;
        btn?.focus();
      }
      break;
    }
  }
};

const handlePeriodKeydown = (event: KeyboardEvent): void => {
  switch (event.key) {
    case "ArrowUp":
    case "ArrowDown": {
      event.preventDefault();
      selectPeriod(selectedPeriod.value === 0 ? 1 : 0);
      nextTick(() => {
        const btn = periodColumn.value?.querySelector(
          "[tabindex='0']"
        ) as HTMLElement | null;
        btn?.focus();
      });
      break;
    }
    case "ArrowLeft": {
      event.preventDefault();
      nextTick(() =>
        focusCenterButton(minuteScroller.value!, focusedMinute.value)
      );
      break;
    }
  }
};

const focusFirstColumn = (): void => {
  nextTick(() => focusCenterButton(hourScroller.value!, focusedHour.value));
};

onMounted(() => {
  nextTick(() => {
    const hs = hourScroller.value!;
    if (selectedHour.value !== null) {
      const index = hourValues.indexOf(selectedHour.value);

      if (index !== -1) {
        scrollToMiddleCopy(hs, hourValues.length, index);
      }
    } else {
      // Start in the middle copy even with no selection
      hs.scrollTop =
        ((hs.scrollHeight - SCROLL_PADDING * 2) / COPY_COUNT) * CENTER_COPY;
    }

    const ms = minuteScroller.value!;
    if (selectedMinute.value !== null) {
      const index = minuteValues.indexOf(selectedMinute.value);

      if (index !== -1) {
        scrollToMiddleCopy(ms, minuteValues.length, index);
      }
    } else {
      ms.scrollTop =
        ((ms.scrollHeight - SCROLL_PADDING * 2) / COPY_COUNT) * CENTER_COPY;
    }

    if (props.autofocus) {
      focusCenterButton(hs, focusedHour.value);
    }
  });
});

defineExpose({ focusFirstColumn });
</script>
